import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { CreateUpdateClientGroupDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { ClientGroupEditorForm } from './ClientGroupEditorForm';

interface ClientGroupModalEditorProps {
  id?: string;
  systemNumber?: number;
  clientGroup: CreateUpdateClientGroupDto;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: (
    clientGroup: CreateUpdateClientGroupDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const ClientGroupModalEditor: React.FC<
  ClientGroupModalEditorProps
> = props => {
  const [clientGroup, setClientGroup] = useState(props.clientGroup);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('clientLng');

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setClientGroup(props.clientGroup);
    }
  }, [props.clientGroup, props.show]);

  const save = async () => {
    setIsLoading(true);
    props
      .onCompleted(clientGroup, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  const propertyChange = (obj: Partial<CreateUpdateClientGroupDto>) => {
    setClientGroup({ ...clientGroup, ...obj });
  };

  return (
    <Modal
      onHide={props.handleClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {props.id
            ? `${t(tk.clientGroup.clientGroupEdit)} ${props.systemNumber}`
            : t(tk.clientGroup.clientGroupAdd)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ClientGroupEditorForm
          clientGroup={clientGroup}
          propertyChange={propertyChange}
        />
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="m-3"
        />
      </Modal.Body>
      <Modal.Footer>
        {props.id && (
          <LastEditorInfo
            id={props.id}
            method={pok.clientGroups.getLastEditor}
          />
        )}
        <Button
          variant="outline-secondary"
          onClick={props.handleClose}
          disabled={isLoading}
        >
          {t(tk.button.close)}
        </Button>
        <Button variant="primary" onClick={save} isLoading={isLoading}>
          {t(tk.button.save)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
